import React, { useRef, useState } from 'react';
import { Input, InputNumber, Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { DOVIZ_OPTIONS, entityOptions } from '../api/financeApi';
import { inputNumberMoneyProps } from '../../../utils/currency';

/**
 * Excel-benzeri satır içi hücre editörü (antd editable-cell deseni).
 *
 * FinansDataTable `components={{ body: { cell: EditableCell } }}` ile tüm body
 * hücrelerini bu bileşenden geçirir; `onCell` tanımlı olmayan kolonlar (İşlem vb.)
 * düz <td> olarak render edilir.
 *
 * Props (onCell'den):
 *  - editing      bu hücre şu an düzenleme modunda mı
 *  - editable     tıklayınca düzenlemeye geçebilir mi
 *  - dataIndex    kolon key'i (colDef.key)
 *  - colDef       kolon konfigürasyonu (tableConfigs formatı)
 *  - record       satır verisi (taslak satırda record.__draft === true)
 *  - onStartEdit  hücreyi düzenleme moduna al
 *  - onSave(v)    değeri kaydet (tarih 'YYYY-MM-DD' string, sayılar number)
 *  - onCancel     düzenlemeyi iptal et
 *  - lookups      entity Select seçenekleri için ortak lookup verisi
 */
function CellEditor({ colDef, record, lookups, onSave, onCancel, persistent }) {
  const initial = record[colDef.key];
  const [val, setVal] = useState(initial === undefined ? null : initial);
  const doneRef = useRef(false);

  // Tek atımlık koruma: kayıt/iptal sonrası unmount sırasında blur'un ikinci kez
  // tetiklenmesini engeller. Taslak (persistent) hücreler sürekli açık kaldığı
  // için korumasız çalışır (her blur/Enter draftRow'a merge eder).
  const save = (v) => {
    if (!persistent) {
      if (doneRef.current) return;
      doneRef.current = true;
    }
    onSave(v === undefined ? null : v);
  };
  const cancel = () => {
    if (!persistent) {
      if (doneRef.current) return;
      doneRef.current = true;
    }
    onCancel();
  };
  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      cancel();
    }
  };

  const type = colDef.type;

  if (type === 'amount' || type === 'number') {
    return (
      <InputNumber
        {...(type === 'amount' ? inputNumberMoneyProps : {})}
        autoFocus={!persistent}
        size="small"
        style={{ width: '100%' }}
        step={type === 'amount' ? 0.01 : 1}
        value={val}
        onChange={setVal}
        onPressEnter={() => save(val)}
        onBlur={() => save(val)}
        onKeyDown={onKeyDown}
      />
    );
  }

  if (type === 'date') {
    return (
      <DatePicker
        autoFocus={!persistent}
        defaultOpen={!persistent}
        size="small"
        style={{ width: '100%' }}
        format="DD.MM.YYYY"
        allowClear={!colDef.required}
        value={val ? dayjs(val) : null}
        onChange={(d) => {
          const v = d ? d.format('YYYY-MM-DD') : null;
          setVal(v);
          save(v);
        }}
        onBlur={() => cancel()}
        onKeyDown={onKeyDown}
      />
    );
  }

  if (type === 'select' || type === 'doviz' || type === 'entity') {
    const opts =
      type === 'doviz'
        ? DOVIZ_OPTIONS
        : type === 'entity'
          ? entityOptions(lookups, colDef.entity, colDef.entityFilter)
          : colDef.options || [];
    // DB'den string gelen değerleri (örn. "1") seçenek değerine eşle
    const matched = opts.find((o) => String(o.value) === String(val));
    return (
      <Select
        autoFocus={!persistent}
        defaultOpen={!persistent}
        size="small"
        style={{ width: '100%', minWidth: 90 }}
        showSearch={type === 'entity'}
        optionFilterProp="label"
        allowClear={!colDef.required}
        options={opts}
        value={matched ? matched.value : val == null || val === '' ? undefined : val}
        onChange={(v) => {
          setVal(v === undefined ? null : v);
          save(v === undefined ? null : v);
        }}
        onBlur={() => cancel()}
        onKeyDown={onKeyDown}
      />
    );
  }

  // 'textarea' / 'text' / default
  return (
    <Input
      autoFocus={!persistent}
      size="small"
      maxLength={colDef.max}
      value={val == null ? '' : val}
      onChange={(e) => setVal(e.target.value)}
      onPressEnter={() => save(val)}
      onBlur={() => save(val)}
      onKeyDown={onKeyDown}
    />
  );
}

export default function EditableCell({
  editing,
  editable,
  dataIndex, // eslint-disable-line no-unused-vars -- onCell sözleşmesinin parçası, td'ye sızmasın
  colDef,
  record,
  children,
  onStartEdit,
  onSave,
  onCancel,
  lookups,
  ...restProps
}) {
  if (!colDef || !record) {
    return <td {...restProps}>{children}</td>;
  }

  if (editing) {
    return (
      <td {...restProps}>
        <CellEditor
          colDef={colDef}
          record={record}
          lookups={lookups}
          onSave={onSave}
          onCancel={onCancel}
          persistent={!!record.__draft}
        />
      </td>
    );
  }

  if (editable) {
    return (
      <td
        {...restProps}
        className={`${restProps.className || ''} nf-editable-cell`.trim()}
        style={{ ...restProps.style, cursor: 'pointer' }}
        title="Düzenlemek için tıklayın"
        onClick={(e) => {
          if (restProps.onClick) restProps.onClick(e);
          if (onStartEdit) onStartEdit();
        }}
      >
        {children}
      </td>
    );
  }

  return <td {...restProps}>{children}</td>;
}
